import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UnicornScene } from './Unicorn/Unicorn';
import HolidayScene from './HolidayLoaders/HolidayScene';
import { resolveLoader } from './HolidayLoaders/holidayLoaderRegistry';
import BravoLogo, { LOGO_LIGHT_SRC } from './BravoLogo';

// Single visible inline loader per window: several grids often load at once
// (details pages with 2+ tables) and a stack of big loaders reads as a bug.
// The first inline loader claims the slot and renders; simultaneous ones render
// nothing until the owner finishes, then the longest-waiting loader is promoted.
// Compact spinners are tiny and fullscreen overlays are input-blocking — both
// stay outside this arbitration and always render.
let slotOwner = null;
const slotWaiters = new Set();

function setFavicon(href) {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export default function BravoLoader({
  loading = true,
  variant = 'inline',
  title = 'LOADING',
  subtitle,
  className = '',
}) {
  useEffect(() => {
    if (!loading) return undefined;
    setFavicon('/img/loading_icon.ico');
    return () => setFavicon('/img/bare_icon.ico');
  }, [loading]);

  const isCompact = variant === 'compact';
  // Resolve which loader to show ONCE per mount so the scene's random traits
  // stay stable. Compact call sites (small inline spinners) always keep the
  // branded mark — the festive scenes are a min(600px) stage.
  const res = useMemo(() => (isCompact ? { type: 'mark', entry: null } : resolveLoader()), [isCompact]);

  // Claim the slot while this loader is visibly loading; release on hide/unmount.
  // useLayoutEffect + pre-paint setState so the owner never flashes hidden and a
  // hidden waiter appears without a paint of the stale state when promoted.
  const participates = variant === 'inline';
  const ownerToken = useRef(null);
  if (ownerToken.current === null) ownerToken.current = {};
  const [visible, setVisible] = useState(!participates);
  useLayoutEffect(() => {
    if (!loading || !participates) return undefined;
    const token = ownerToken.current;
    const tryClaim = () => {
      if (slotOwner === null) slotOwner = token;
      const owns = slotOwner === token;
      setVisible(owns);
      return owns;
    };
    if (!tryClaim()) slotWaiters.add(tryClaim);
    return () => {
      slotWaiters.delete(tryClaim);
      if (slotOwner === token) {
        slotOwner = null;
        const next = slotWaiters.values().next().value;
        if (next) {
          slotWaiters.delete(next);
          next();
        }
      }
    };
  }, [loading, participates]);

  if (!loading) return null;
  if (participates && !visible) return null;

  const holiday = res.type === 'holiday' ? res.entry : null;
  const unicorn = res.type === 'unicorn';

  let sceneEl = null;
  if (holiday) {
    if (holiday.builder) {
      sceneEl = <HolidayScene builder={holiday.builder} />;
    } else {
      const SceneComp = holiday.Component;
      sceneEl = SceneComp ? <SceneComp /> : null;
    }
  }

  return (
    <div className={('bravo-ds ' + className).trim()}>
      <div className={'loading ' + variant + (unicorn ? ' unicorn' : '') + (holiday ? ' holiday' : '')}>
        {holiday ? (
          <div className='hol-frame'>
            {sceneEl}
            <div className='hol-mark'>
              <img
                src={LOGO_LIGHT_SRC}
                alt='BARE'
              />
              <div className='hol-ring'></div>
              <div className='hol-ring r2'></div>
            </div>
          </div>
        ) : (
          <div className='mark'>
            {unicorn ? <UnicornScene /> : <BravoLogo />}
            <div className='ring'></div>
            <div className='ring r2'></div>
          </div>
        )}
        {holiday && !isCompact ? (
          <a
            className='hol-link'
            href={'https://www.google.com/search?q=' + encodeURIComponent(holiday.term)}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={'Learn about ' + holiday.label}
          >
            {holiday.label} <span aria-hidden='true'>↗</span>
          </a>
        ) : null}
        {!isCompact ? (
          <div className='ltext'>
            <b>{title}</b>
            {subtitle ? <span>{subtitle}</span> : null}
          </div>
        ) : null}
        {!isCompact ? (
          <div className='lbar'>
            <i></i>
          </div>
        ) : null}
      </div>
    </div>
  );
}
